import {
  Button,
  Dialog,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
//@ts-ignore
import UserSearch from '../../../shared-components/UserSearch';
import EncouragementService from '../../../shared-services/Notation/EncouragementService';
import PrivacyPolicy from '../../../shared-components/PrivacyPolicy';
import { IEncouragementType } from '../../../shared-interfaces/Notation/IEncouragementType';
import { ICoreUser } from '../../../shared-interfaces/User/ICoreUser';
import { Role } from '../../../shared-types/Roles';
import CreateNotation from './CreateNotation';
import ReactGA from 'react-ga';
import InstitutionContext, {
  hasFeature,
} from '../../contexts/InstitutionContext';
import ErrorBox from '../../ErrorBox';
import BeaconRte, {
  convertBeaconRteToHtml,
  EditorValueHasText,
  EmptyRteValue,
} from '../../BeaconRte';
import NotationAttributeContext from '../../contexts/NotationAttributesContext';
import OutlinedFormControl from '../../OutlinedFormControl';

export interface IEncouragementNotationForm {
  reset: (isSuccess?: boolean) => void;
  isPreview?: boolean;
  encouragementAudiencePermission: boolean;
  encouragementShowPermission: boolean;
}

const EncouragementNotationForm = (props: IEncouragementNotationForm) => {
  const institution = useContext(InstitutionContext);
  const notationContext = useContext(NotationAttributeContext);
  const { encouragementTypes } = notationContext;
 

  const [rteValue, setRteValue] = useState(EmptyRteValue);
  const [students, setStudents] = useState<ICoreUser[]>([]);
  const [audience, setAudience] = useState<string>(
    props.encouragementAudiencePermission ? '' : 'All',
  );
  const [type, setType] = useState<IEncouragementType>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorOpen, setErrorOpen] = useState<boolean>(false);
  const [overFifty, setOverFifty] = useState(false);

  useEffect(() => {
    if (isSubmitting) {
      ReactGA.event({
        category: 'NotationWidget',
        action: 'submit',
        label: JSON.stringify({
          insitutionId: institution.institution.id,
          notationType: 'encouragement',
        }),
        nonInteraction: false,
      });
    }
  }, [isSubmitting]);

  const selectedStyle = { backgroundColor: '#06c', color: 'white' };

  return (
    <>
      <CreateNotation name="Encouragement" reset={props.reset}>
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <FormControl style={{ width: '100%' }}>
              <UserSearch
                label="Name or External Id of Student(s) (Max 10)"
                onSelect={x => setStudents(x)}
                roleRestriction={[Role.Student, Role.PeerAdvisor]}
                value={students}
                maxSearchLength={10}
                onSearchLengthError={setOverFifty}
                multiple
              />
              <FormHelperText>Required</FormHelperText>
              {overFifty && (
                <Typography color="error">
                  Encouragements can only be created for up to 10 students at a
                  time. Only the first 10 entered will be used.
                </Typography>
              )}
            </FormControl>
          </Grid>
          {props.encouragementAudiencePermission && (
            <Grid item>
              <OutlinedFormControl
                label="Audience"
                style={{ width: '100%' }}
                required
              >
                <RadioGroup
                  aria-label="Audience"
                  name="audience"
                  value={audience}
                  onChange={v => setAudience(v.target.value)}
                >
                  <FormControlLabel
                    value="Private"
                    control={<Radio />}
                    label="Private"
                  />
                  <FormControlLabel
                    value="FullNetwork"
                    control={<Radio />}
                    label="Professional staff + peer advisors"
                  />
                </RadioGroup>
              </OutlinedFormControl>
            </Grid>
          )}
          <Grid item>
            <FormControl style={{ width: '100%' }}>
              <InputLabel id="type-label">Encouragement Type</InputLabel>
              <Select
                labelId="type-label"
                // id="demo-simple-select"
                value={type ? type.id : ''}
                onChange={(
                  x: React.ChangeEvent<{
                    name?: string | undefined;
                    value: number;
                  }>,
                ) => {
                  setType(
                    encouragementTypes?.find(y => y.id === x.target.value),
                  );
                }}
              >
                {encouragementTypes?.map(x => (
                  <MenuItem key={x.id} value={x.id}>
                    {x.name}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Required</FormHelperText>
            </FormControl>
          </Grid>
          <Grid item>
            <BeaconRte
              required
              label="Description"
              value={rteValue}
              onChange={setRteValue}
            />
           </Grid>
           <Grid item>
             <PrivacyPolicy policyHtml={notationContext.assessmentFerpaStatement} />
           </Grid>
          <Grid item>
            <div style={{ float: 'right' }}>
              <Button
                variant="text"
                color="secondary"
                onClick={() => props.reset()}
                aria-label="Cancel"
              >
                Cancel
              </Button>
              <Button
                aria-label="Create Encouragement"
                variant="contained"
                color="primary"
                style={{ marginRight: '1em' }}
                onClick={() => {
                  if (props.isPreview) return;
                  setIsSubmitting(true);
                  // validate
                  EncouragementService.createMultiple({
                    studentIds: students?.map(x => x.id),
                    audienceType: audience,
                    details: convertBeaconRteToHtml(rteValue),
                    typeId: type?.id,
                  })
                    .then(() => props.reset(true))
                    .catch(() => {
                      setIsSubmitting(false);
                      setErrorOpen(true);
                    });
                }}
                disabled={
                  !students ||
                  students.length === 0 ||
                  !audience ||
                  !type ||
                  (rteValue && !EditorValueHasText(rteValue))
                }
              >
                Create Encouragement
              </Button>
            </div>
          </Grid>
        </Grid>
        <Dialog onClose={() => setErrorOpen(false)} open={errorOpen}>
          <ErrorBox
            message="Failed to Create Encouragement(s)"
            showCorporateContact
          />
          <DialogActions>
            <Button aria-label="Close" onClick={() => setErrorOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </CreateNotation>
      {isSubmitting && <LinearProgress />}
    </>
  );
};

export default EncouragementNotationForm;
